import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import Uppy, { Meta } from '@uppy/core';

import { VpgrooveModule } from '@engineering/vpgroove-angular';
import { FileUploadOptionsModel, FileUploadResponseModel, FileUploadService } from '@genesis-frontend/genesis-utilities';

@Component({
    selector: 'file-upload',
    standalone: true,
    imports: [CommonModule, VpgrooveModule],
    templateUrl: './file-upload.component.html',
    styleUrl: './file-upload.component.css',
    providers: [FileUploadService]
})
export class FileUploadComponent {
    @Input() triggerButtonSelector = `file-upload-trigger-button-${this.getRandomNumber()}`;
    @Input() dashboardSelector = `dashboard-selector-${this.getRandomNumber()}`;
    @Input() customOptions?: Partial<FileUploadOptionsModel>;
    @Input() showImages = false;
    @Input() imageLayoutDirection: 'row' | 'column' | 'grid' = 'row';
    @Input() imageSize = 300;
    @Input() buttonText = 'Open Uploader';

    @Output() uploadResponse = new EventEmitter<FileUploadResponseModel[]>();

    uppyInstance: any;
    uppyImages: FileUploadResponseModel[] | undefined;
    maxFilesAllowed = 0;
    defaultOptions = [] as unknown as FileUploadOptionsModel;

    constructor(private fileUploadService: FileUploadService) {}

    ngOnInit() {
        this.defaultOptions = this.fileUploadService.getDefaultOptions(this.dashboardSelector, this.triggerButtonSelector);

        if (this.customOptions) {
            this.mergeOptions(this.defaultOptions, this.customOptions);
        }

        this.maxFilesAllowed = this.defaultOptions?.general?.restrictions?.maxNumberOfFiles;
        this.initUpload(this.defaultOptions).then((uppy) => {
            this.uppyInstance = uppy;
        });

        this.fileUploadService.onDashboardModalClose().subscribe(async() => {
            await this.refreshUppyInstance(this.defaultOptions);
        });
    }

    private async refreshUppyInstance(defaultOptions: FileUploadOptionsModel) {
        this.uppyInstance.destroy();
        this.uppyInstance = null;
        this.uppyInstance = await this.initUpload(defaultOptions);
    }

    private async initUpload(defaultOptions: FileUploadOptionsModel): Promise<Uppy<Meta, Record<string, never>>> {
        const currentCount = this.uppyImages?.length || 0;
        const remaining = Math.max(this.maxFilesAllowed - currentCount, 0);

        defaultOptions.general.restrictions.maxNumberOfFiles = remaining;
        return await this.fileUploadService.init(defaultOptions, (response: FileUploadResponseModel[]) => {
            const successful = response.filter((file) => file.status === 'success');

            this.uppyImages = [...(this.uppyImages || []), ...successful];
            this.uploadResponse.emit(response);
        });
    }

    private mergeOptions(target: any, source: any) {
        for (const key of Object.keys(source)) {
            if (source[key] instanceof Object && key in target) {
                this.mergeOptions(target[key], source[key]);
            } else {
                target[key] = source[key];
            }
        }
    }

    removeImage(image: FileUploadResponseModel): void {
        this.uppyImages = this.uppyImages?.filter((i) => i !== image);
        this.refreshUppyInstance(this.defaultOptions);
        this.uploadResponse.emit(this.uppyImages);
    }

    clearAllImages(): void {
        this.uppyImages = [];
        this.refreshUppyInstance(this.defaultOptions);
        this.uploadResponse.emit(this.uppyImages);
    }

    private getRandomNumber(): string {
        return Math.floor(Math.random() * 1000).toString();
    }
}
